<template>
  <div class="col-12">
    <div class="card-body px-0 pb-2">
      <div class="container-fluid px-0">
        <div class="row mb-4 g-2 px-4 pt-4">
          <div class="col-12 col-md-6">
            <label class="form-label visually-hidden">{{ $t('select_date') }}</label>
            <input 
              type="date" 
              class="form-control form-control-sm" 
              v-model="date"
              :max="new Date().toISOString().split('T')[0]"
              @change="fetchTeachers"
            >
          </div>
        </div>
      </div>

      <div class="card mb-5" style="margin-top: 2rem; overflow: visible">
        <div class="card-header p-0 position-relative mt-n4 mx-3 z-index-2" style="height: 60px">
          <div class="bg-gradient-primary shadow-primary border-radius-lg pt-4 pb-3 h-100">
            <div class="d-flex justify-content-between align-items-center">
              <h6 class="text-white text-capitalize ps-3 pe-3 mb-0">{{ $t('save_attendance') }}</h6>
            </div>
          </div>
        </div>

        <div class="table-responsive p-4">
          <LoadingSpinner v-if="isLoading" :message="$t('loading_teachers')" />
          
          <div v-if="!isLoading && teachers.length > 0 && Object.keys(attendances).length === teachers.length">
            <!-- Mobile view -->
            <div class="d-block d-md-none">
              <div v-for="teacher in teachers" :key="'mobile-'+teacher.id" class="card mb-3">
                <div class="card-body">
                  <h6 class="mb-3 fw-medium">{{ teacher.username }}</h6>
                  
                  <div class="mb-3">
                    <label class="form-label small">{{ $t('attendance.status') }}</label>
                    <select 
                      class="form-select form-select-sm borderless-input" 
                      v-model="attendances[teacher.id].status"
                      :disabled="isSaving"
                    >
                      <option value="present">{{ $t('present') }}</option>
                      <option value="absent">{{ $t('absent') }}</option>
                      <option value="late">{{ $t('late') }}</option>
                      <option value="excused">{{ $t('excused') }}</option>
                      <option value="on_leave">{{ $t('on_leave') }}</option>
                    </select>
                  </div>
                  
                  <div>
                    <label class="form-label small">{{ $t('notes') }}</label>
                    <input 
                      type="text" 
                      class="form-control form-control-sm borderless-input" 
                      v-model="attendances[teacher.id].notes"
                      :placeholder="$t('notes')"
                      :disabled="isSaving"
                    >
                  </div>
                </div>
              </div>
              
              <div class="mt-4 d-flex flex-column gap-2">
                <button 
                  @click="saveAttendance"
                  class="btn btn-primary btn-sm"
                  :disabled="!date || teachers.length === 0 || isSaving"
                >
                  <span v-if="isSaving">
                    <i class="fas fa-spinner fa-spin me-2"></i>
                    {{ $t('saving') }}
                  </span>
                  <span v-else>
                    <i class="fas fa-save ms-2"></i>
                    {{ $t('save_attendance') }}
                  </span>
                </button>
                <button
                  @click="handleCancel"
                  class="btn btn-outline-secondary btn-sm"
                  :disabled="isSaving"
                >
                  <i class="fas fa-times ms-2"></i>
                  {{ $t('cancel') }}
                </button>
              </div>
            </div>
            
            <!-- Desktop view -->
            <div class="d-none d-md-block">
              <table class="table align-items-center mb-0">
                <thead>
                  <tr>
                    <th class="text-uppercase text-xxs font-weight-bolder opacity-7">{{ $t('teacher_name') }}</th>
                    <th class="text-uppercase text-xxs font-weight-bolder opacity-7">{{ $t('status') }}</th>
                    <th class="text-uppercase text-xxs font-weight-bolder opacity-7">{{ $t('notes') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="teacher in teachers" :key="teacher.id">
                    <td>
                      <div class="d-flex px-3 py-1">
                        <div class="d-flex flex-column justify-content-center">
                          <h6 class="mb-0 text-sm">{{ teacher.username }}</h6>
                        </div>
                      </div>
                    </td>
                    <td>
                      <select 
                        class="form-select borderless-input" 
                        v-model="attendances[teacher.id].status"
                        style="min-width: 120px;"
                        :disabled="isSaving"
                      >
                        <option value="present">{{ $t('present') }}</option>
                        <option value="absent">{{ $t('absent') }}</option>
                        <option value="late">{{ $t('late') }}</option>
                        <option value="excused">{{ $t('excused') }}</option>
                        <option value="on_leave">{{ $t('on_leave') }}</option>
                      </select>
                    </td>
                    <td>
                      <input 
                        type="text" 
                        class="form-control borderless-input" 
                        v-model="attendances[teacher.id].notes"
                        :placeholder="$t('notes')"
                        :disabled="isSaving"
                      >
                    </td>
                  </tr>
                </tbody>
              </table>

              <div class="mt-4 d-flex gap-2">
                <button 
                  @click="saveAttendance"
                  class="btn btn-primary btn-sm flex-grow-1"
                  :disabled="!date || teachers.length === 0 || isSaving"
                >
                  <span v-if="isSaving">
                    <i class="fas fa-spinner fa-spin me-2"></i>
                    {{ $t('saving') }}
                  </span>
                  <span v-else>
                    <i class="fas fa-save ms-2"></i>
                    {{ $t('save_attendance') }}
                  </span>
                </button>
                <button
                  @click="handleCancel"
                  class="btn btn-outline-secondary btn-sm flex-grow-1"
                  :disabled="isSaving"
                >
                  <i class="fas fa-times ms-2"></i>
                  {{ $t('cancel') }}
                </button>
              </div>
            </div>
          </div>

          <template v-else-if="teachers.length > 0">
            <div class="text-center py-4">
              <div class="spinner-border text-primary spinner-border-sm" role="status">
                <span class="visually-hidden">{{ $t('loading_teachers') }}</span>
              </div>
              <p class="mt-2 text-sm">{{ $t('preparing_attendance') }}</p>
            </div>
          </template>

          <template v-else>
            <div class="text-center py-4 text-muted">
              <i class="fas fa-users-slash fa-2x mb-2"></i>
              <p class="text-sm">{{ $t('no_teachers') }}</p>
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useToast } from 'vue-toastification';
import { useI18n } from 'vue-i18n';
import api from '@/api';

const { t } = useI18n();
const router = useRouter();
const toast = useToast();

const date = ref(new Date().toISOString().split('T')[0]);
const teachers = ref([]);
const attendances = ref({});
const isLoading = ref(false);
const isSaving = ref(false);

const fetchTeachers = async () => {
  if (!date.value) {
    toast.warning(t("choose_date_first"));
    return;
  }

  isLoading.value = true;
  teachers.value = [];
  attendances.value = {};

  try {
    const response = await api.get('/teachers');
    if (response.data?.data) {
      teachers.value = response.data.data;

      // Initialize attendance
      teachers.value.forEach(teacher => {
        attendances.value[teacher.id] = {
          teacher_id: teacher.id,
          status: 'present',
          notes: ''
        };
      });

      // Check existing attendance
      try {
        const attendanceResponse = await api.get('/teacher-attendances', {
          params: { date: date.value, with_teachers: true }
        });

        if (attendanceResponse.data.data.length > 0) {
          toast.warning(t("existing_attendance_warning"));

          attendanceResponse.data.data.forEach(record => {
            if (record.teacher && attendances.value[record.teacher.id]) {
              attendances.value[record.teacher.id] = {
                ...attendances.value[record.teacher.id],
                id: record.id,
                status: record.status,
                notes: record.notes || ''
              };
            }
          });
        }
      } catch (error) {
        console.error("Error checking existing attendance:", error);
      }
    }
  } catch (error) {
    toast.error(t("attendance_save_error"));
    console.error("Error fetching teachers:", error);
  } finally {
    isLoading.value = false;
  }
};

const saveAttendance = async () => {
  if (!date.value) {
    toast.warning(t("choose_date_first"));
    return;
  }

  if (teachers.value.length === 0) {
    toast.warning(t("no_teachers_to_save"));
    return;
  }

  const attendanceData = Object.values(attendances.value)
    .filter(att => att.teacher_id)
    .map(att => ({
      teacher_id: att.teacher_id,
      status: att.status,
      notes: att.notes || ''
    }));

  if (attendanceData.length === 0) {
    toast.error(t("no_teachers_to_save"));
    return;
  }

  isSaving.value = true;
  try {
    const payload = { date: date.value, attendances: attendanceData };
    const response = await api.post('/teacher-attendances', payload);

    toast.success(response.data.message || t("attendance_saved_success"));
    router.push('/teacher-attendances');
  } catch (error) {
    if (error.response?.data?.errors?.attendances) {
      error.response.data.errors.attendances.forEach(err => toast.error(err));
    } else if (error.response?.data?.message) {
      toast.error(error.response.data.message);
    } else {
      toast.error(t("attendance_save_error"));
    }
    console.error("Error saving attendance:", error);
  } finally {
    isSaving.value = false;
  }
};

const handleCancel = () => {
  if (!isSaving.value) {
    router.push('/teacher-attendances');
  }
};

onMounted(() => {
  fetchTeachers();
});
</script>
